// Depth-sensing placement policy (candidate 1 — WebXR Depth API).
//
// Why this exists: ARCore's plane hit-test intersects the viewer ray
// with DETECTED planes only. A car body is never a detected plane
// (dark, curved, reflective), so the ray sails past the real wheel and
// lands on the wall / ground behind it — the placed wheel ends up
// ~30 cm behind the car's wheel face. The WebXR Depth API samples the
// camera depth map, which DOES register the car surface. This module
// is the pure, testable half: given both candidate hits, decide which
// one placement should use.
//
// All functions are pure — no WebXR runtime — so they run under
// vitest. The browser-only sampling lives in WebXRDriver.

import type { HitResult } from '@/ar/wheel-renderer'

interface Vec3 {
  x: number
  y: number
  z: number
}

/** ARCore's depth map is unreliable below ~20 cm and past ~8 m
 *  (depth-from-motion resolution degrades with distance). Samples
 *  outside this window are discarded rather than trusted. */
export const MIN_DEPTH_M = 0.2
export const MAX_DEPTH_M = 8

/** When the depth sample and the plane hit agree within this margin,
 *  the plane hit wins: plane-attached anchors are stabler than world
 *  anchors, so we only abandon them when they are meaningfully wrong. */
export const DEPTH_PLANE_AGREEMENT_M = 0.05


/** Is a raw depth-map sample usable for placement? Rejects null /
 *  NaN / Infinity and anything outside the trusted range. */
export function isUsableDepthMeters(depthM: number | null): boolean {
  if (depthM == null) return false
  if (!Number.isFinite(depthM)) return false
  return depthM >= MIN_DEPTH_M && depthM <= MAX_DEPTH_M
}

/** Sample the depth map every Nth frame (20 Hz on a 60 Hz loop). A
 *  cpu-optimized depth read costs a per-call buffer transfer; doing it
 *  every frame during SLAM warm-up starved ARCore's feature tracking
 *  on the Xiaomi 14T Pro (2026-08-20 UAT: coaching stuck at ~30%). */
export const DEPTH_SAMPLE_EVERY_N_FRAMES = 3

/** Gate for the per-frame depth read: never during SLAM warm-up
 *  (tracking not ready), then only every Nth frame. Placement reads a
 *  cached sample, so 20 Hz freshness (≤50 ms stale) is plenty. */
export function shouldSampleDepth({
  frameIndex,
  trackingReady,
}: {
  frameIndex: number
  trackingReady: boolean
}): boolean {
  if (!trackingReady) return false
  return frameIndex % DEPTH_SAMPLE_EVERY_N_FRAMES === 0
}

/** A contact lift below this is implausible for any real wheel — the
 *  smallest catalog wheels are ~0.3m diameter. A bbox-measured lift
 *  under the floor means the measurement failed (empty box, missing
 *  body node), not that the wheel is tiny. */
export const MIN_PLAUSIBLE_CONTACT_LIFT_M = 0.05

/** Shared smoothing time-constant for the depth-placement UX (gauge
 *  line AND preview wheel): alpha = 1 − e^(−dt/tau) per frame ⇒ ~95%
 *  of a step absorbed in 3·tau = 300ms — the accepted display delay
 *  that trades raw-depth sporadicness for a smooth visual. */
export const DEPTH_TWEEN_TAU_MS = 100

/** Vertical clearance between the green ground line and the wheel
 *  body's bottom — visual room for the tire (2026-08-21). Applied to
 *  preview AND placement so pre-tap == post-tap.
 *
 *  This is the WHOLE of the wheel's height contract: Y comes from the
 *  floor estimate for every hit source, so the body always rests
 *  exactly this far above the line the gauge draws, whatever surface
 *  the ray struck. Tightened 5cm → 4cm on 2026-08-22. */
export const TIRE_CLEARANCE_M = 0.04

/** Fixed bias moving a depth-measured point AWAY from the camera, so
 *  the wheel lands on the real wheel's face plane rather than in front
 *  of it.
 *
 *  Field observation 2026-08-24: depth placement is consistently
 *  5-10cm proud of the face. In front is the better failure direction —
 *  the reprojection error a viewer sees grows with distance from the
 *  true plane and a near-side error is the smaller one — but the plane
 *  is more accurate pushed back, so the bias takes 5cm of it out.
 *
 *  Applied to DEPTH-sourced hits only. The plane path's error runs the
 *  other way: its ray tunnels past the unlit, curved car body to the
 *  wall behind it, which is the whole reason depth placement exists.
 *  Biasing that backwards would deepen the bug it was built to fix. */
export const DEPTH_PUSH_BACK_M = 0.05

/** Moves a point further from the camera along the HORIZONTAL view
 *  direction.
 *
 *  Horizontal by construction, for two reasons that point the same way.
 *  Y is owned entirely by the floor estimate — a wheel's height comes
 *  from the ground, never from where the ray struck — so any vertical
 *  component of the bias is discarded downstream anyway. And the
 *  remaining horizontal part would then shrink by cos(pitch), making
 *  the correction depend on how far down the user happens to be
 *  aiming. Projecting first makes 5cm mean 5cm at every aim angle.
 *
 *  Returns the point unchanged when there is no usable direction (the
 *  camera is directly above it) or no usable distance. There is no
 *  "away" to move toward in those cases, and inventing one slides the
 *  wheel across the car's flank. */
export function pushBackFromCamera({
  point,
  cameraPos,
  distanceM,
}: {
  point: Vec3
  cameraPos: Vec3
  distanceM: number
}): Vec3 {
  if (!Number.isFinite(distanceM) || distanceM === 0) return point
  const dx = point.x - cameraPos.x
  const dz = point.z - cameraPos.z
  const horizontalLength = Math.hypot(dx, dz)
  if (!(horizontalLength > 1e-6)) return point
  return {
    x: point.x + (dx / horizontalLength) * distanceM,
    y: point.y,
    z: point.z + (dz / horizontalLength) * distanceM,
  }
}

/** Contact lift arbitration: prefer the MEASURED bbox lift, fall back
 *  to the dimension-derived lift (known face radius × applied scale —
 *  the same numbers that size the occluder ring) when the measurement
 *  is implausible or non-finite. 2026-08-20 round 6: on-device the
 *  bbox path resolved ~0 on the real GLB and the wheel sank by its
 *  radius; two independent estimators make that failure self-healing
 *  and the divergence loggable. */
export function resolveContactLift(
  bboxLiftM: number,
  dimensionLiftM: number,
): number {
  if (Number.isFinite(bboxLiftM) && bboxLiftM >= MIN_PLAUSIBLE_CONTACT_LIFT_M) {
    return bboxLiftM
  }
  return dimensionLiftM
}

/** World point at `depthM` along the (normalised) camera-forward ray.
 *  The depth sample is taken at the viewport CENTER, where the view
 *  ray and the optical axis coincide — so walking the forward vector
 *  by the sampled depth lands exactly on the sensed surface. */
export function computeDepthWorldPoint(
  cameraPos: Vec3,
  cameraForward: Vec3,
  depthM: number,
): Vec3 {
  return {
    x: cameraPos.x + cameraForward.x * depthM,
    y: cameraPos.y + cameraForward.y * depthM,
    z: cameraPos.z + cameraForward.z * depthM,
  }
}

function distance(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const dz = a.z - b.z
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/** Chooses the placement hit between the depth-map sample and the
 *  plane hit-test result.
 *
 *  - Depth meaningfully IN FRONT of the plane (by more than
 *    DEPTH_PLANE_AGREEMENT_M) → depth. This is the car-in-front-of-
 *    wall case the feature exists for.
 *  - Agreement within the margin → plane (stabler anchor, not wrong).
 *  - Depth BEHIND the plane → plane. Depth overshoot on glossy dark
 *    paint is a known depth-map noise mode; the closer surface wins.
 *  - Only one available → that one. Neither → null (placement is
 *    refused upstream with the standard toast).
 *
 *  The returned HitResult carries `source` so placeWheel can pick the
 *  anchor kind: 'depth' → manufactured world anchor (a plane-attached
 *  anchor would glue the wheel to the WRONG plane), 'plane' → the
 *  existing plane-attached path. */
export function chooseDepthPlacement({
  depthHit,
  planeHit,
  cameraPos,
}: {
  depthHit: HitResult | null
  planeHit: HitResult | null
  cameraPos: Vec3
}): HitResult | null {
  if (depthHit == null && planeHit == null) return null
  if (depthHit == null) {
    return { ...planeHit!, source: 'plane' }
  }
  if (planeHit == null) {
    return { ...depthHit, source: 'depth' }
  }
  const depthDist = distance(cameraPos, depthHit.position)
  const planeDist = distance(cameraPos, planeHit.position)
  if (planeDist - depthDist > DEPTH_PLANE_AGREEMENT_M) {
    return { ...depthHit, source: 'depth' }
  }
  return { ...planeHit, source: 'plane' }
}
